import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { TRAITS, STYLE_META, fmtNum, fmtMoney } from "@/data/gameData";
import { ArrowLeft, Loader2, Gamepad2, Star, Lock, Crown, Flame } from "lucide-react";

export default function Profile() {
  const { http, user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([http.get("/me/summary"), http.get("/me/history")])
      .then(([s, h]) => { setSummary(s.data); setHistory(h.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [http]);

  const maxTrait = summary ? Math.max(1, ...TRAITS.map((t) => summary.profile_traits?.[t.key] || 0)) : 1;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-5 py-8">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F43F5E]" /></div>
        ) : (
          <>
            {/* header */}
            <div className="flex items-center gap-4">
              <span className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F43F5E] to-[#8B5CF6] grid place-items-center text-2xl font-black font-display">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
              <div>
                <h1 className="font-display font-black text-3xl tracking-tighter">{user?.name}</h1>
                <p className="text-sm text-slate-400">{user?.email}</p>
              </div>
            </div>

            {/* stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
              <Stat label="Histórias" value={fmtNum(summary?.games || 0)} icon={Gamepad2} color="#06B6D4" />
              <Stat label="Sequência" value={`${summary?.streak || 0}`} icon={Flame} color="#F59E0B" />
              <Stat label="Pontos totais" value={fmtNum(summary?.total_score || 0)} icon={Star} color="#FACC15" />
              <Stat label="Conquistas" value={`${summary?.achievements?.filter((a) => a.unlocked).length || 0}/${summary?.achievements?.length || 9}`} icon={Crown} color="#F43F5E" />
            </div>

            {/* Meu jeito de decidir */}
            <div data-testid="profile-estilo" className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-4">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">Meu jeito de decidir</p>
              {summary?.estilo ? (
                <>
                  <p className="font-display font-black text-2xl">{summary.estilo.nome}</p>
                  <p className="text-sm text-slate-300 mt-1 mb-5">{summary.estilo.label}</p>
                  <div className="space-y-3">
                    {STYLE_META.map((s) => {
                      const v = summary.estilo.counts?.[s.key] || 0;
                      const total = summary.estilo.total || 1;
                      return (
                        <div key={s.key}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-300">{s.emoji} {s.label}</span>
                            <span className="font-semibold" style={{ color: s.color }}>{v}</span>
                          </div>
                          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                            <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${(v / total) * 100}%` }}
                              transition={{ duration: 0.6 }} style={{ background: s.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-4">Um retrato do seu estilo — para reflexão e diversão, não é diagnóstico.</p>
                </>
              ) : (
                <p className="text-sm text-slate-500">Jogue algumas histórias para revelar o seu jeito de decidir.</p>
              )}
            </div>

            {/* persona + traits */}
            <div className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-4">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">Seu perfil de decisões</p>
              {summary?.persona ? (
                <p className="font-display font-black text-2xl mb-5">{summary.persona.emoji} {summary.persona.nome}</p>
              ) : (
                <p className="text-sm text-slate-500 mb-4">Jogue para revelar seu perfil.</p>
              )}
              <div className="space-y-3">
                {TRAITS.map((t) => {
                  const v = summary?.profile_traits?.[t.key] || 0;
                  return (
                    <div key={t.key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-300">{t.emoji} {t.label}</span>
                        <span className="font-semibold" style={{ color: t.color }}>{v}</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                        <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${(v / maxTrait) * 100}%` }}
                          transition={{ duration: 0.6 }} style={{ background: t.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* achievements */}
            <div className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs uppercase tracking-widest text-slate-500">Conquistas</p>
                <button onClick={() => navigate("/conquistas")} className="text-xs text-[#06B6D4]">Ver todas →</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(summary?.achievements || []).map((a) => (
                  <div key={a.id} data-testid={`ach-${a.id}`}
                    className={`relative flex flex-col items-center gap-1 p-4 rounded-2xl border text-center ${a.unlocked ? "bg-white/5 border-[#F59E0B]/30" : "bg-white/5 border-white/10 opacity-50"}`}>
                    <span className={`text-3xl ${a.unlocked ? "" : "grayscale"}`}>{a.emoji}</span>
                    <span className="text-[11px] text-slate-300 leading-tight">{a.nome}</span>
                    {!a.unlocked && <Lock className="w-3 h-3 text-slate-500 absolute top-2 right-2" />}
                  </div>
                ))}
              </div>
            </div>

            {/* history */}
            <div className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-4">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">Histórico</p>
              {history.length ? (
                <div className="space-y-2">
                  {history.map((h) => (
                    <button key={h.session_id} data-testid="history-item" onClick={() => navigate(`/resultado/${h.session_id}`)}
                      className="w-full flex items-center justify-between gap-3 p-3 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors text-left">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{h.persona?.emoji} {h.titulo}</p>
                        <p className="text-xs text-slate-500">{fmtMoney(h.money)}</p>
                      </div>
                      <span className="font-display font-black text-[#F43F5E]">{fmtNum(h.score)}</span>
                    </button>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-500">Nenhuma partida ainda.</p>}
            </div>

            {/* premium teaser */}
            <div data-testid="profile-premium" className="rounded-3xl p-6 bg-gradient-to-br from-[#8B5CF6]/15 to-[#F43F5E]/10 border border-[#8B5CF6]/25 mt-4">
              <div className="flex items-center gap-2 text-[#C4B5FD]">
                <Crown className="w-5 h-5" />
                <p className="font-display font-black text-lg">E SE FOSSE COMIGO<span className="text-[#F43F5E]">+</span></p>
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-slate-300">EM BREVE</span>
              </div>
              <ul className="mt-3 space-y-1.5 text-sm text-slate-300">
                <li>✦ Histórias exclusivas e cenários especiais</li>
                <li>✦ Desafios extras além do desafio do dia</li>
                <li>✦ Estatísticas avançadas do seu jeito de decidir</li>
                <li>✦ Experiência sem anúncios</li>
              </ul>
              <button disabled className="mt-4 w-full rounded-2xl py-3 bg-white/5 border border-white/10 text-slate-400 font-semibold cursor-not-allowed">
                Disponível em breve
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl p-4 bg-[#0F0F14] border border-white/10 text-center">
      <Icon className="w-5 h-5 mx-auto mb-2" style={{ color }} />
      <p className="font-display font-black text-xl">{value}</p>
      <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}
